import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useAppBrandLibrary from "src/features/AppBranding/hooks/useAppBrandLibrary/useAppBrandLibrary";
import useActiveAppBrand from "src/features/AppBranding/hooks/useActiveAppBrand/useActiveAppBrand";
import useMakeAppBrandsActive from "src/features/AppBranding/hooks/useMakeAppBrandsActive/useMakeAppBrandsActive";
import useMakeAppBrandsInactive from "src/features/AppBranding/hooks/useMakeAppBrandsInactive/useMakeAppBrandsInactive";
import useDeleteAppBrands from "src/features/AppBranding/hooks/useDeleteAppBrands/useDeleteAppBrands";

function mapBrandsToContentTableItems(
	activeBrand: AppBrand | Record<string, never> | undefined,
	libraryBrands: AppBrand[] | undefined
): ContentTableItem[] {
	const live: ContentTableItem[] =
		activeBrand && "id" in activeBrand
			? [
					{
						id: activeBrand.id,
						name: activeBrand.name,
						createdBy: activeBrand.createdBy
							? `${activeBrand.createdBy.firstName} ${activeBrand.createdBy.lastName}`
							: undefined,
						lastUpdated: activeBrand.updatedAt,
						isActive: true,
						order: 1
					}
				]
			: [];

	const draft = (libraryBrands ?? []).map((b) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt ?? b.createdAt,
		isActive: false
	}));

	return [...live, ...draft];
}

const AppBranding = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	const { data: activeBrand } = useActiveAppBrand();
	const { data: appBrandLibrary } = useAppBrandLibrary();

	const { mutate: makeBrandsActive } = useMakeAppBrandsActive();
	const { mutate: makeBrandsInactive } = useMakeAppBrandsInactive();
	const { mutate: deleteAppBrands, isLoading: isDeletingBrands } =
		useDeleteAppBrands();

	const items = mapBrandsToContentTableItems(activeBrand, appBrandLibrary);

	return (
		<Page>
			<ContentTable
				items={items}
				showOrder={false}
				onRowClick={(item) =>
					navigate(
						`/vendor-sponsorship/app-branding/brands/new-app-branding-form/${item.id}`
					)
				}
				onMakeLive={(item) => makeBrandsActive([Number(item.id)])}
				onMakeDraft={(item) => makeBrandsInactive([Number(item.id)])}
				onDelete={(item) => setItemToDelete(item)}
				createPath="/vendor-sponsorship/app-branding/brands/new-app-branding-form"
				createLabel="Create Brand"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete brand?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteAppBrands([Number(itemToDelete.id)]);
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeletingBrands }}
			/>
		</Page>
	);
};

export default AppBranding;
